import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { toast } from "sonner";
import { ChevronsUpDown, Check, ShieldCheck, Save } from "lucide-react";
import { NATIONALITIES } from "@/lib/nationalities";
import { cn } from "@/lib/utils";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Reasonable international phone shape: optional +, 4–20 digits, spaces
// / dashes / parens allowed for readability.
const PHONE_RE = /^\+?[0-9()\-\s]{7,25}$/;

const EMPTY = {
  full_legal_name: "",
  email: "",
  mobile: "",
  home_address: "",
  date_of_birth: "",
  nationality: "",
};

/** Utility: today in YYYY-MM-DD in the user's local timezone (for max date). */
function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function validate(values, { emailEditable }) {
  const errors = {};
  if (!values.full_legal_name || values.full_legal_name.trim().length < 2) {
    errors.full_legal_name = "Full legal name is required.";
  }
  if (emailEditable) {
    if (!values.email) errors.email = "Email is required.";
    else if (!EMAIL_RE.test(values.email.trim())) errors.email = "Enter a valid email address.";
  }
  if (!values.mobile) errors.mobile = "Mobile number is required.";
  else if (!PHONE_RE.test(values.mobile.trim())) {
    errors.mobile = "Use an international format, e.g. +44 7700 900123.";
  }
  if (!values.home_address || values.home_address.trim().length < 5) {
    errors.home_address = "Home address is required.";
  }
  if (!values.date_of_birth) errors.date_of_birth = "Date of birth is required.";
  else if (values.date_of_birth > todayISO()) {
    errors.date_of_birth = "Date of birth cannot be in the future.";
  }
  if (!values.nationality) errors.nationality = "Nationality is required.";
  return errors;
}

/**
 * PersonalDetailsForm
 *
 * Used both stand-alone on the candidate profile (edit) AND embedded inside
 * the invite dialog when a manager creates a new candidate.
 *
 * Props:
 *   value              — controlled { full_legal_name, email, mobile, ... }
 *   onChange           — controlled setter
 *   accountEmail       — the account email (shown read-only when not editable)
 *   emailEditable      — false on profile edit (account email is immutable),
 *                        true on invite (candidate's email captured on invite)
 *   errors             — external validation errors (merged with local ones on submit)
 *   embed              — when true, hides the internal save button (parent submits)
 *   workerId           — required when embed=false; used to PATCH
 *   onSaved(details)   — callback after successful save
 */
export default function PersonalDetailsForm({
  value,
  onChange,
  accountEmail,
  emailEditable = false,
  embed = false,
  workerId,
  onSaved,
}) {
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // If the caller doesn't supply email but the account has one, mirror it.
  useEffect(() => {
    if (!emailEditable && accountEmail && value.email !== accountEmail) {
      onChange({ ...value, email: accountEmail });
    }
  }, [accountEmail, emailEditable]);

  const errors = useMemo(() => validate(value, { emailEditable }), [value, emailEditable]);
  const shownErr = (key) => (touched[key] || touched.__all ? errors[key] : undefined);

  const setField = (key, v) => onChange({ ...value, [key]: v });
  const markTouched = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

  const submit = async () => {
    setTouched({ __all: true });
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_legal_name: value.full_legal_name.trim(),
        mobile: value.mobile.trim(),
        home_address: value.home_address.trim(),
        date_of_birth: value.date_of_birth,
        nationality: value.nationality.trim(),
      };
      const { data } = await api.patch(`/workers/${workerId}/personal-details`, payload);
      setSavedAt(Date.now());
      toast.success("Personal details saved");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error("Could not save", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="personal-details-form">
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        <ShieldCheck className="h-4 w-4 text-teal-700 shrink-0 mt-0.5" />
        <div>
          Held as sensitive personal data. Only this candidate, their reviewer,
          and workspace managers can view or edit these details.
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Full legal name"
          testid="pd-full-legal-name"
          required
          error={shownErr("full_legal_name")}
        >
          <Input
            value={value.full_legal_name}
            onChange={(e) => setField("full_legal_name", e.target.value)}
            onBlur={() => markTouched("full_legal_name")}
            data-testid="pd-input-full-legal-name"
            placeholder="e.g. Sarah Jane Patel"
            autoComplete="name"
          />
        </Field>

        <Field
          label="Email address"
          testid="pd-email"
          required
          error={shownErr("email")}
          hint={!emailEditable ? "This is the account email — change it from account settings." : undefined}
        >
          <Input
            type="email"
            value={emailEditable ? value.email : accountEmail || value.email}
            onChange={(e) => emailEditable && setField("email", e.target.value)}
            onBlur={() => markTouched("email")}
            readOnly={!emailEditable}
            className={!emailEditable ? "bg-slate-50 text-slate-600 cursor-not-allowed" : ""}
            data-testid="pd-input-email"
            placeholder="sarah@example.com"
            autoComplete="email"
          />
        </Field>

        <Field
          label="Mobile number"
          testid="pd-mobile"
          required
          error={shownErr("mobile")}
          hint="Use international format including country code."
        >
          <Input
            type="tel"
            value={value.mobile}
            onChange={(e) => setField("mobile", e.target.value)}
            onBlur={() => markTouched("mobile")}
            data-testid="pd-input-mobile"
            placeholder="+44 7700 900123"
            autoComplete="tel"
            inputMode="tel"
          />
        </Field>

        <Field
          label="Date of birth"
          testid="pd-dob"
          required
          error={shownErr("date_of_birth")}
        >
          <Input
            type="date"
            value={value.date_of_birth}
            onChange={(e) => setField("date_of_birth", e.target.value)}
            onBlur={() => markTouched("date_of_birth")}
            max={todayISO()}
            data-testid="pd-input-dob"
            autoComplete="bday"
          />
        </Field>

        <Field
          label="Nationality"
          testid="pd-nationality"
          required
          error={shownErr("nationality")}
          className="sm:col-span-2"
        >
          <NationalitySelect
            value={value.nationality}
            onChange={(v) => { setField("nationality", v); markTouched("nationality"); }}
          />
        </Field>

        <Field
          label="Home address"
          testid="pd-home-address"
          required
          error={shownErr("home_address")}
          className="sm:col-span-2"
        >
          <Textarea
            value={value.home_address}
            onChange={(e) => setField("home_address", e.target.value)}
            onBlur={() => markTouched("home_address")}
            data-testid="pd-input-home-address"
            rows={3}
            placeholder="Street, city, postcode, country"
            autoComplete="street-address"
          />
        </Field>
      </div>

      {!embed && (
        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="pd-save"
            className="bg-teal-700 hover:bg-teal-800 rounded-full h-10 px-5"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {submitting ? "Saving…" : "Save personal details"}
          </Button>
          {savedAt && (
            <span
              data-testid="pd-saved-hint"
              className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold"
            >
              <Check className="h-3.5 w-3.5" /> Saved just now
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, required, error, hint, children, testid, className }) {
  return (
    <div className={cn("space-y-1.5", className)} data-testid={`${testid}-field`}>
      <Label className="label-caps">
        {label}
        {required && <span className="text-rose-600 ml-1" aria-hidden>*</span>}
      </Label>
      {children}
      {error && (
        <p className="text-xs text-rose-600 font-medium" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
      {!error && hint && (
        <p className="text-xs text-slate-500">{hint}</p>
      )}
    </div>
  );
}

function NationalitySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="pd-input-nationality"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-1",
            !value && "text-slate-400"
          )}
        >
          {value || "Search nationality…"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" data-testid="pd-nationality-search" />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {NATIONALITIES.map((n) => (
                <CommandItem
                  key={n}
                  value={n}
                  onSelect={() => { onChange(n); setOpen(false); }}
                  data-testid={`pd-nationality-opt-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === n ? "opacity-100 text-teal-700" : "opacity-0")} />
                  {n}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { validate as validatePersonalDetails, EMPTY as EMPTY_PERSONAL_DETAILS };
